/**
 * Per-tab imperative side-table for in-flight forecast executions, keyed by
 * runId. Holds non-serializable handles (AbortController, SSE reader) that must
 * NOT live in reducer state. Successor to streamRegistry.js (traceId-keyed);
 * here a run owns its one current execution, so runId is the natural key.
 *
 * Instantiate once per App via a lazy ref init (Ring 0-wire), not per render.
 */
export function createExecTable() {
  /** @type {Map<string, object>} runId -> { abortController?, reader?, ...handle } */
  const entries = new Map();

  return {
    /** Claim a run's execution slot. False if falsy runId or already active. */
    claim(runId, handle = {}) {
      if (!runId) return false;
      if (entries.has(runId)) return false;
      entries.set(runId, { ...handle });
      return true;
    },
    /** @returns {object|null} a shallow copy of the stored handle, or null.
     *  Copied (like streamRegistry.get) so callers can't mutate internal state
     *  by writing to the returned object; the handle refs (AbortController/reader)
     *  are shared, which is intended (abort/read the same execution). */
    get(runId) {
      const e = entries.get(runId);
      return e ? { ...e } : null;
    },
    /** @returns {boolean} whether runId currently holds an execution. */
    has(runId) {
      return entries.has(runId);
    },
    /** @returns {number} how many runs currently hold an execution slot.
     *  Used to reconcile the global `loading` flag: 0 ⇒ nothing is in flight. */
    size() {
      return entries.size;
    },
    /** Free the slot. @returns {boolean} whether an entry was removed. */
    release(runId) {
      return entries.delete(runId);
    },
    /** Abort the run's AbortController (if any) and free the slot. @returns {boolean} */
    abort(runId) {
      const e = entries.get(runId);
      if (!e) return false;
      if (e.abortController && typeof e.abortController.abort === "function")
        e.abortController.abort();
      return entries.delete(runId);
    },
  };
}
